'use client';

import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Minus, CheckCircle, ArrowLeft } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface GuestEntry {
  name: string;
  dietary: string;
  dietaryOther: string;
}

interface RSVPEditFormValues {
  attending: 'yes' | 'no' | '';
  primaryName: string;
  primaryEmail: string;
  primaryDietary: string;
  message: string;
  plusOnes: GuestEntry[];
  fridayEvening: boolean;
  sundayBrunch: boolean;
  sundayWalk: boolean;
}

interface RSVPData {
  id: string;
  attending: string;
  primary_name: string;
  primary_email: string | null;
  primary_dietary: string;
  message: string | null;
  plus_ones: GuestEntry[];
  friday_evening: boolean;
  sunday_brunch: boolean;
  sunday_walk: boolean;
}

interface RSVPEditFormProps {
  rsvp: RSVPData;
  onBack: () => void;
  onSaveSuccess: () => void;
}

const dietaryOptions = [
  { value: 'none', label: 'No requirements' },
  { value: 'nut-allergy', label: 'Nut Allergy' },
  { value: 'gluten-free', label: 'Gluten Free' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'other', label: 'Other (please mention in message)' },
];

export default function RSVPEditForm({ rsvp, onBack, onSaveSuccess }: RSVPEditFormProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const supabase = createClient();

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<RSVPEditFormValues>({
    defaultValues: {
      attending: (rsvp.attending as 'yes' | 'no') || 'yes',
      primaryName: rsvp.primary_name || '',
      primaryEmail: rsvp.primary_email || '',
      primaryDietary: rsvp.primary_dietary || 'none',
      message: rsvp.message || '',
      plusOnes: (rsvp.plus_ones || []).map((p) => ({
        name: p.name || '',
        dietary: p.dietary || 'none',
        dietaryOther: p.dietaryOther || '',
      })),
      fridayEvening: rsvp.friday_evening || false,
      sundayBrunch: rsvp.sunday_brunch || false,
      sundayWalk: rsvp.sunday_walk || false,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'plusOnes' });
  const attending = watch('attending');

  const onSubmit = async (data: RSVPEditFormValues) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const { error } = await supabase
        .from('rsvp_submissions')
        .update({
          attending: data.attending,
          primary_name: data.primaryName,
          primary_email: data.primaryEmail || null,
          primary_dietary: data.primaryDietary,
          message: data.message || null,
          plus_ones: data.plusOnes,
          friday_evening: data.fridayEvening,
          sunday_brunch: data.sundayBrunch,
          sunday_walk: data.sundayWalk,
        })
        .eq('id', rsvp.id);

      if (error) {
        setSaveError('Something went wrong saving your changes. Please try again.');
        return;
      }

      setSaved(true);
      setTimeout(() => {
        onSaveSuccess();
      }, 1800);
    } catch {
      setSaveError('Something went wrong. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="max-w-2xl">
        <div className="border border-border bg-off-white p-10 text-center">
          <CheckCircle size={32} className="text-olive mx-auto mb-6" strokeWidth={1} />
          <p className="font-serif text-2xl text-faded-navy mb-3">Changes saved.</p>
          <p className="font-sans text-sm text-stone">Your RSVP details have been updated.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      {/* Back link */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-sans text-stone hover:text-faded-navy transition-colors duration-300 mb-10"
      >
        <ArrowLeft size={13} />
        Back to Dashboard
      </button>

      <div className="mb-10">
        <p className="section-label mb-2">Edit RSVP</p>
        <h2 className="font-serif text-3xl text-faded-navy">Update your details</h2>
        <p className="font-sans text-sm text-stone mt-3 leading-relaxed">
          Make any changes below and save when you're done.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-12">
        {/* Attendance */}
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone mb-5">Attendance</p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { value: 'yes', label: 'Joyfully accepts', sublabel: 'I will be there' },
              { value: 'no', label: 'Regretfully declines', sublabel: 'I cannot attend' },
            ].map((option) => (
              <label
                key={`attend-${option.value}`}
                className={`relative flex flex-col items-center justify-center p-6 border cursor-pointer transition-all duration-300 ${
                  attending === option.value
                    ? 'border-faded-navy bg-off-white' :'border-border hover:border-warm-sand'
                }`}
              >
                <input
                  type="radio"
                  value={option.value}
                  className="sr-only"
                  {...register('attending', { required: true })}
                />
                <span className="font-serif text-lg text-faded-navy mb-1">{option.label}</span>
                <span className="text-xs font-sans text-stone">{option.sublabel}</span>
                {attending === option.value && (
                  <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-faded-navy" />
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Name & Email */}
        <div className="space-y-8">
          <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone">Your Details</p>
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="input-field"
              {...register('primaryName', { required: 'Please enter your name' })}
            />
            {errors.primaryName && (
              <p className="mt-2 text-xs font-sans text-red-500">{errors.primaryName.message}</p>
            )}
          </div>
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
              Email Address
            </label>
            <input
              type="email"
              className="input-field"
              {...register('primaryEmail')}
            />
          </div>
        </div>

        {/* Dietary */}
        {attending === 'yes' && (
          <>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone mb-5">Dietary Requirements</p>
              <p className="text-xs font-sans text-stone/70 italic mb-4">
                🌿 Please note: all catering on the day will be vegan.
              </p>
              <select className="input-field bg-transparent cursor-pointer" {...register('primaryDietary')}>
                {dietaryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Plus ones */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone">Additional Guests</p>
                <button
                  type="button"
                  onClick={() => append({ name: '', dietary: 'none', dietaryOther: '' })}
                  className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-sans text-faded-navy hover:text-stone transition-colors duration-300"
                >
                  <Plus size={13} />
                  Add Guest
                </button>
              </div>

              {fields.length === 0 && (
                <p className="text-xs font-sans text-stone/60 italic">
                  Add any plus-ones or family members here.
                </p>
              )}

              {fields.map((field, index) => (
                <div key={field.id} className="border border-border p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <p className="text-xs tracking-[0.15em] uppercase font-sans text-stone">Guest {index + 1}</p>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-stone/50 hover:text-red-400 transition-colors duration-200"
                      aria-label="Remove guest"
                    >
                      <Minus size={13} />
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">Full Name</label>
                    <input
                      type="text"
                      className="input-field"
                      {...register(`plusOnes.${index}.name` as const, { required: 'Please enter the guest name' })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">Dietary Requirements</label>
                    <p className="text-xs font-sans text-stone/70 italic mb-2">🌿 All catering will be vegan.</p>
                    <select
                      className="input-field bg-transparent cursor-pointer"
                      {...register(`plusOnes.${index}.dietary` as const)}
                    >
                      {dietaryOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>

            {/* Weekend events */}
            <div className="space-y-6">
              <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone">Weekend Events</p>
              <p className="text-xs font-sans text-stone/70 leading-relaxed">
                Update your attendance for the weekend gatherings below.
              </p>
              {[
                {
                  field: 'fridayEvening' as const,
                  label: 'Friday 28th August — Evening Apéritif',
                  note: 'Galions Quay · 17:00–19:00',
                },
                {
                  field: 'sundayBrunch' as const,
                  label: 'Sunday 30th August — Morning on the Beach',
                  note: 'Blackpool Sands · 10:00 onwards',
                },
                {
                  field: 'sundayWalk' as const,
                  label: 'Sunday 30th August — Afternoon Walk',
                  note: 'South West Coast Path · 15:00',
                },
              ].map((item) => (
                <label key={item.field} className="flex items-start gap-4 cursor-pointer group">
                  <div className="relative mt-0.5">
                    <input type="checkbox" className="sr-only" {...register(item.field)} />
                    <div className={`w-4 h-4 border transition-all duration-300 flex items-center justify-center ${
                      watch(item.field) ? 'border-faded-navy bg-faded-navy' : 'border-border group-hover:border-warm-sand'
                    }`}>
                      {watch(item.field) && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#FAF8F3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="font-sans text-sm text-faded-navy">{item.label}</p>
                    <p className="font-sans text-xs text-stone mt-0.5">{item.note}</p>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}

        {/* Message */}
        <div>
          <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
            A message for George & Boadecea
          </label>
          <p className="text-xs font-sans text-stone/60 mb-3">Entirely optional — but always treasured.</p>
          <textarea
            rows={4}
            className="input-field resize-none"
            placeholder="Share your wishes, a memory, or simply say hello..."
            {...register('message')}
          />
        </div>

        {saveError && (
          <p className="text-xs font-sans text-red-500">{saveError}</p>
        )}

        <div className="flex items-center gap-4 pt-2">
          <button
            type="submit"
            disabled={isSaving}
            className="flex-1 py-4 text-xs tracking-[0.3em] uppercase font-sans bg-faded-navy text-buttermilk hover:bg-deep-navy transition-all duration-400 disabled:opacity-60 flex items-center justify-center gap-3"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving
              </>
            ) : (
              'Save Changes'
            )}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="px-8 py-4 text-xs tracking-[0.3em] uppercase font-sans border border-border text-stone hover:border-faded-navy hover:text-faded-navy transition-all duration-300"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
